/* eslint-disable functional/prefer-readonly-type */

import type { ImageSourcePropType, ImageURISource } from 'react-native';
import { PixelRatio } from 'react-native';

/**
 * Returns asset/source based on current screen DPI.
 */
export function getImageForCurrentScreen(
  source?: ImageSourcePropType[] | ImageSourcePropType,
) {
  if (!Array.isArray(source)) return source;

  const screenPixelRatio = Math.ceil(PixelRatio.get());
  const lastAvailableImage = source[source.length - 1];

  return source[screenPixelRatio - 1] ?? lastAvailableImage;
}

/**
 * A tiny utility that adds (if needed) the `https` prefix to remote asset links that
 * do not have the protocol.
 *
 * NOTE: Some remote links exclude protocol from their links so that protocol can be
 *       dynamically determined based on the consumer page protocol to avoid mixed content.
 *
 *       However, this will not work on native devices, where the correct protocol
 *       must be explicitly provided.
 *
 * @example
 *
 * withHttps('//example.com/image.png') -> 'https://example.com/image.png'
 * withHttps('https://example.com/image.png') -> 'https://example.com/image.png'
 */
export function withHttpsProtocol(
  imageSource: ImageSourcePropType | undefined,
) {
  // invalid source
  if (!imageSource) return;

  // skip local assets
  if (typeof imageSource === 'number') return imageSource;

  // multiple assets case
  if (Array.isArray(imageSource)) {
    return imageSource.map(addHttpsProtocol);
  }

  return addHttpsProtocol(imageSource);
}

function addHttpsProtocol(remoteImageSource: ImageURISource): ImageURISource {
  const isLinkWithoutProtocol = remoteImageSource.uri?.startsWith('//');

  // skip valid sources
  if (!isLinkWithoutProtocol) return remoteImageSource;

  return { ...remoteImageSource, uri: `https:${remoteImageSource.uri}` };
}
